/**
 * WizardSidebar
 *
 * Left sidebar showing step navigation with completion status.
 * Allows jumping to visited steps. Supports dynamic steps based on mode.
 */

import { type WizardStep, type WizardStepConfig, type WizardMode } from '@/types/wizard';
import { cn } from '@/lib/utils';
import { Check, ChevronRight, Building2, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface WizardSidebarProps {
  steps: WizardStepConfig[];
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  visitedSteps: WizardStep[];
  onStepClick: (step: WizardStep) => void;
  mode: WizardMode;
}

export function WizardSidebar({
  steps,
  currentStep,
  completedSteps,
  visitedSteps,
  onStepClick,
  mode,
}: WizardSidebarProps) {
  return (
    <aside className="hidden md:block w-64 border-r bg-muted/30 p-4">
      <nav className="space-y-1">
        {/* Mode indicator */}
        {mode === 'development_project' && (
          <div className="mb-4 px-2">
            <Badge variant="secondary" className="gap-1.5">
              <Building2 className="h-3 w-3" />
              Development
            </Badge>
          </div>
        )}

        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">
          Steps
        </h3>
        {steps.map((step) => {
          const isCurrent = step.id === currentStep;
          const isCompleted = completedSteps.includes(step.id);
          const isVisited = visitedSteps.includes(step.id);
          const canClick = isVisited || isCompleted;

          return (
            <button
              key={step.id}
              onClick={() => canClick && onStepClick(step.id)}
              disabled={!canClick}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                isCurrent && "bg-primary text-primary-foreground",
                !isCurrent && canClick && "hover:bg-muted cursor-pointer",
                !isCurrent && !canClick && "opacity-50 cursor-not-allowed"
              )}
            >
              {/* Step indicator */}
              <div
                className={cn(
                  "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                  isCompleted && !isCurrent && "bg-green-500 text-white",
                  isCurrent && "bg-primary-foreground text-primary",
                  !isCompleted && !isCurrent && "bg-muted-foreground/20 text-muted-foreground"
                )}
              >
                {isCompleted && !isCurrent ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  step.number
                )}
              </div>

              {/* Step label */}
              <span
                className={cn(
                  "flex-1 text-sm font-medium truncate",
                  !isCurrent && !isCompleted && "text-muted-foreground"
                )}
              >
                {step.title}
              </span>

              {/* Current indicator */}
              {isCurrent && (
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
